import styled from '@emotion/styled';
import type { SimCard } from '@racemap/sdk/schema/simCard';
import { humanizedFileSize } from '@racemap/utilities/functions/humanizedFileSize';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import type { PathInto } from '@racemap/utilities/types/utils';
import { Typography } from 'antd';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useInspectedTracker, useSimCard } from '../../../lib/customHooks';
import { type Column, type Entry, SimpleTable } from '../../BasicComponents/SimpleTable';
import { UserLabel } from '../../BasicComponents/UserLabel';
import { SimCardStatusBadge } from '../../SimCardsManager/SimCardStatusBadge';
import { castDateTime } from '../../utils/timeUtils';

const { Text } = Typography;

interface SIMInfoEntry extends Entry {
  id: Exclude<keyof SimCard, 'device'>;
  value: string | number | boolean;
}

export const SimCardTab: FC = () => {
  const inspectedTracker = useInspectedTracker();
  // todo rewrite get sim route to work also with iccids
  const { sim } = useSimCard({
    simId: inspectedTracker?.simCard?.id.toHexString(),
    iccid: inspectedTracker?.meta?.iccid,
  });
  if (inspectedTracker == null || sim == null) return null;

  const data = Object.entries(sim).map(([key, value]) => ({
    value,
    id: key,
  })) as Array<SIMInfoEntry>;

  const columns = [
    {
      header: 'Info',
      dataKey: 'id',
      cellRenderer: (c, r) => (isNotEmptyString(r.id) && SIMInfoLable[r.id]) || c,
      key: 'id',
    } as Column<SIMInfoEntry, 'id'>,
    {
      header: 'Value',
      dataKey: 'value',
      cellRenderer: (c, r) => {
        if (c == null) return;
        switch (r.id) {
          case 'id':
          case 'iccid':
          case 'msisdn':
          case 'imsi':
            return (
              <Text code copyable>
                {c.toString()}
              </Text>
            );
          case 'friendlyName':
            return <Text>{c.toString()}</Text>;
          case 'status':
            return (
              <SimCardStatusBadge
                status={sim.status}
                lastActivationAt={sim.lastActivationAt}
                lastTerminationAt={sim.lastTerminationAt}
              />
            );
          case 'userId':
            return <UserLabel userId={c.toString()} />;
          case 'updatedAt':
          case 'createdAt':
          case 'lastActivationAt':
          case 'lastTerminationAt':
          case 'lastConnectionAt':
          case 'lastDisconnectionAt':
            if (typeof c === 'boolean') return '--';
            return castDateTime(c).toLocaleString(DateTime.DATETIME_FULL);
          case 'currentDataUsage':
            return humanizedFileSize(c as number);
          default:
            return `${c}`;
        }
      },
      key: 'value',
    } as Column<SIMInfoEntry, 'value'>,
  ] as Array<Column<Entry, PathInto<SIMInfoEntry>>>;

  return <Table columns={columns} data={data} />;
};

const Table = styled(SimpleTable)`
  border-radius: unset;
  border-top: unset;
  `;

const SIMInfoLable = {
  id: 'ID',
  iccid: 'ICCID',
  imsi: 'IMSI',
  msisdn: 'MSISDN',
  friendlyName: 'Friendly Name',
  lastIpAddress: 'Last IP Address',
  status: 'Status',
  userId: 'User',
  updatedAt: 'Updated At',
  createdAt: 'Created At',
  lastActivationAt: 'Last Activation',
  lastTerminationAt: 'Last Termination',
  lastConnectionAt: 'Last Connection',
  lastDisconnectionAt: 'Last Disconnection',
  currentDataUsage: 'Data Usage',
  group: 'Group',
};
